import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { useUserData } from 'src/hooks/common/useUserData';
import { globalDataStore, jianInfoAtom, jianInfoErrorAtom, jianSoshikiListAtom, prevUserIdAtom } from 'src/jotai/atom';
import { getJianinfo } from 'src/utilities/restApi/common/jianinfo';
import { getJianSoshiki } from 'src/utilities/restApi/kanri/jianSoshiki';


/**
 * jianInfo: 事案情報
 *   初期値: undefind
 *   データなし: null
 *   データあり: JianInfo
 */
export const useJianInfo = () => {
  const { userData } = useUserData();
  const [jianInfo, setJianInfo] = useAtom(jianInfoAtom, { store: globalDataStore });
  const [soshikis, setSoshikis] = useAtom(jianSoshikiListAtom, { store: globalDataStore });
  const [isError, setIsError] = useAtom(jianInfoErrorAtom, { store: globalDataStore });  

  // 前回のuserIdを保持するAtom
  const [prevUserId, setPrevUserId] = useAtom(prevUserIdAtom, { store: globalDataStore });

  useEffect(() => {
    // NOTE: useJianInfoは複数のコンポーネント（AuthAreaTemplate,AzureSigninPage）から参照されており
    // NOTE: useEffectが複数回発火することがある。
    // NOTE: 同じユーザIDでrefreshJianInfoを重複実行することを避けるため
    // NOTE: 前回useEffectを実行したときのユーザIDを保存し、
    // NOTE: 前回useEffectからユーザIDが変わっていなければrefreshJianInfoは実行しないようにしている
    const currentUserId = userData?.userId;
    if (userData && currentUserId !== prevUserId) {
      // 事案情報を更新
      refreshJianInfo();
      // 現在のユーザーIDを記録
      setPrevUserId(currentUserId);
    }
  }, [userData]);

  const refreshJianInfo = () => {
    return getJianinfo()
      .then((response) => {
        if (response.data?.jianinfo) {
          setJianInfo({
            jianId: response.data.jianinfo.jianid,
            jianName: response.data.jianinfo.jianname,
            jianMode: response.data.jianinfo.jianmode,
            disasterTypeNumber: response.data.jianinfo.disaster_type_number,
            jianStart: response.data.jianinfo.jianstart,
            jianEnd: response.data.jianinfo.jianend ?? undefined,
            jianReset: response.data.jianinfo.jianreset ?? undefined,
          });

          getJianSoshiki({ jianid: response.data.jianinfo.jianid })
            .then((response) => {
              if (response.data?.soshiki_list) {
                setSoshikis(response.data.soshiki_list);
              }
            })
            .catch((error) => {
              throw error;
            });
        } else {
          setJianInfo(null);
          setSoshikis([]);
        }
      })
      .catch(() => setIsError(true));
  };

  const jianOngoing: () => boolean | undefined = useCallback(() => {
    let result: boolean | undefined = undefined;

    if (jianInfo !== undefined) {
      result = jianInfo !== null && jianInfo.jianEnd === undefined;
    }

    return result;
  }, [jianInfo]);

  const jianResettable: () => boolean | undefined = useCallback(() => {
    let result: boolean | undefined = undefined;

    if (jianInfo !== undefined) {
      result = jianInfo !== null && jianInfo.jianEnd !== undefined && jianInfo.jianReset === undefined;
    }

    return result;
  }, [jianInfo]);

  const jianAllDone: () => boolean | undefined = useCallback(() => {
    let result: boolean | undefined = undefined;

    if (jianInfo !== undefined) {
      result = jianInfo === null || (jianInfo.jianEnd !== undefined && jianInfo.jianReset !== undefined);
    }

    return result;
  }, [jianInfo]);

  return { jianInfo, jianOngoing, jianResettable, jianAllDone, refreshJianInfo, isError, soshikis };
};
